import { Navigation } from "@/components/layout/Navigation";
import Hero from "@/components/marketing/Hero";
import Footer from "@/components/layout/Footer";
import { Shield, Zap, Pen, Megaphone, Ghost, Smile, HandCoins } from 'lucide-react';
import Testimonial from "@/components/marketing/Testimonial";
import CTAButton from "@/components/marketing/CTAButton";
import { MarketingArchetypeCard, MarketingRewriteSuggestionsDemo } from '@/components/marketing-demos';

const ForProfessionals = () => {
  return (
    <div className="min-h-screen">
      <Navigation showAuth={true} className="mt-4" />

      {/* Hero Section */}
      <Hero
        badge="AI-Assisted Context Aware Copywriting"
        headline={
          <>
            <span className="font-serif font-medium italic text-primary">
              Ship copy faster.
            </span>{' '}
            Increase your ROI.
          </>
        }
        description={<><span className="block mb-4">Arcitext saves your writing business hours of dealing with brand alignment and analysis. </span><span className="block">Manage your brand voices, pitch new clients, and upsell content ideas with Arcitext's Discovery tool.</span></>}
        heroVisuals={<div className="mt-8 w-full relative lg:mt-0 flex flex-col lg:-translate-y-1/4 gap-4"><div className="hidden lg:block lg:absolute lg:z-10 lg:translate-y-full lg:-bottom-8 lg:-left-12"><MarketingArchetypeCard archetype="creator" state="primary" /></div><div className="hidden lg:block lg:absolute lg:z-5 lg:hover:z-20 lg:-bottom-8 lg:translate-y-full lg:right-12 "><MarketingArchetypeCard archetype="hero" state="secondary" /></div><MarketingRewriteSuggestionsDemo /></div>}
      >
        <CTAButton
          to="/auth"
          event="For Pros Hero CTA"
        />
      </Hero>

      <Testimonial quote="Whether I'm editing an ebook, writing an article, a blog post, a tweet, etc. I tend to reach for Arcitext because it's that good. It rivals other mature products I've used in the past." author="Jon C. Phillips" link="https://joncphillips.com" linkText="Read the tweet" />

      {/* Professional Use Cases */}
      <section className="pb-16 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-12">
            <h2 className="text-3xl font-bold text-foreground">Built for Copywriting & Content Pros</h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="glass-card flex flex-col items-center p-6 rounded-2xl space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                  <Pen className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Copywriters</h3>
                <p className="text-muted-foreground">
                  Maintain each client's unique brand voice across copy, while keeping tabs on their purpose and target audience.
                </p>
              </div>

              <div className="glass-card flex flex-col items-center p-6 rounded-2xl space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                  <Ghost className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Ghostwriters</h3>
                <p className="text-muted-foreground">
                  Writing for someone else? Quickly analyze their writing style, and align your copy with their voice. They'll never notice.
                </p>
              </div>

              <div className="glass-card flex flex-col items-center p-6 rounded-2xl space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Content Teams</h3>
                <p className="text-muted-foreground">
                  Manage multiple brand profiles, and output copy that resonates across any channel: Email, LinkedIn, Blogs, and Website Copy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Helps You */}
      <section className="py-16 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Go From Furious to Fast. <span className="font-serif font-bold italic text-primary">In Minutes.</span>
            </h2>

            <div className="space-y-8">
              <div className="flex gap-6 items-start">
                <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-semibold text-sm">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Analyze Brand Profile</h3>
                  <p className="text-muted-foreground">
                    Provide your clients' URLs and writing samples. Add cultural context. Arcitext will analyze and profile the brand, and let you focus on what you do best.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-semibold text-sm">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Write With Purpose</h3>
                  <p className="text-muted-foreground">
                    Add context to your copy: brand profile, purpose, channel and SEO Keywords. Arcitext will give you relevant feedback, based on your goals.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-semibold text-sm">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Pitch New Content</h3>
                  <p className="text-muted-foreground">
                    Arcitext's Discovery tool helps you discover relevant news, and suggest angles for your client's target audience. Simply add relevant RSS feeds, and upsell new content ideas to your clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-16">
            <CTAButton
              to="/auth"
              event="For Pros How It Works CTA"
            />
          </div>
        </div>
      </section>

      {/* Professional Benefits */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-12">
            <h2 className="text-3xl font-bold text-foreground">Level Up Your Writing Business & Marketing</h2>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto text-left">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Faster Turnaround</h3>
                </div>
                <p className="text-muted-foreground">
                  Reduce revision cycles by catching voice mismatches early. You'll produce on-brand content faster, with fewer client revisions.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Consistent Quality</h3>
                </div>
                <p className="text-muted-foreground">
                  Maintain the same high standard across all content, regardless of the channel you're writing for.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Smile className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Client Satisfaction</h3>
                </div>
                <p className="text-muted-foreground">
                  Deliver content that clients love because it truly sounds like their brand. No more "this doesn't sound like us" feedback.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <HandCoins className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">Upsell Content Ideas</h3>
                </div>
                <p className="text-muted-foreground">
                  Generate new revenue by pitching new content ideas and angles, based on your client's target audience interests.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      {/* <section className="py-16 bg-muted/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl font-bold text-foreground">
            The ROI is Clear
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-2">
              <div className="text-3xl font-bold text-primary">50%</div>
              <div className="text-muted-foreground">Fewer revisions</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-primary">3x</div>
              <div className="text-muted-foreground">Faster onboarding</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-primary">100%</div>
              <div className="text-muted-foreground">Brand consistency</div>
            </div>
          </div>
        </div>
      </section> */}

      <Footer />
    </div>
  );
};

export default ForProfessionals;
